import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/utils-app";
import { EnvelopeIcon as Envelope, ArrowLeftIcon as ArrowLeft, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

/** Forgot-password page.
 *
 *  Always shows the same generic confirmation regardless of whether the
 *  email is registered (matches backend behaviour, which never leaks
 *  account existence). On success we render a success state instead of
 *  redirecting so the user can re-read the instructions if needed.
 */
export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      // Backend always returns 200 for this endpoint, so the only way to land
      // here is a transport / 5xx failure — surface that.
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6 font-manrope">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img
            src="/logo-256.png"
            alt="Armstrong K9 Registry"
            className="h-28 w-28 rounded-full object-contain"
          />
        </div>
        <div
          className="bg-white border border-[#E5E5E5] rounded-xl p-8 shadow-[0_8px_30px_rgba(90,107,86,0.04)]"
          data-testid="forgot-password-page"
        >
          {sent ? (
            <div className="text-center" data-testid="forgot-password-sent">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FBF7EC] border border-[#E8D9A6] mb-4">
                <CheckCircle size={28} weight="duotone" className="text-gold" />
              </div>
              <h1 className="font-spectral text-2xl mb-2 tracking-tight">Check your inbox</h1>
              <p className="text-sm text-[#6B6B6B] mb-6">
                If an account exists for <strong className="text-ink">{email}</strong>,
                we've emailed a link to reset your password. The link expires in
                60 minutes and can only be used once.
              </p>
              <p className="text-xs text-[#6B6B6B] mb-6">
                Didn't get the email? Check your spam folder or{" "}
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="text-ink font-semibold underline underline-offset-2"
                  data-testid="forgot-password-retry"
                >
                  try again
                </button>
                .
              </p>
              <Button
                type="button"
                onClick={() => navigate("/login")}
                variant="outline"
                className="w-full rounded-full h-11 border-[#E5E5E5]"
                data-testid="forgot-password-back-btn"
              >
                <ArrowLeft size={14} weight="bold" className="mr-2" />
                Back to sign in
              </Button>
            </div>
          ) : (
            <>
              <h1 className="font-spectral text-3xl mb-1 tracking-tight">Forgot password?</h1>
              <p className="text-sm text-[#6B6B6B] mb-6">
                Enter the email address you registered with. We'll send you a
                one-time link to choose a new password.
              </p>
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    autoFocus
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-password-email-input"
                    className="mt-1 rounded-lg p-3 focus:ring-2 focus:ring-[#0E0E0E]/20 focus:border-[#0E0E0E]"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full h-11"
                  data-testid="forgot-password-submit-btn"
                >
                  <Envelope size={16} weight="duotone" className="mr-2" />
                  {loading ? "Sending…" : "Email me a reset link"}
                </Button>
              </form>
              <p className="text-sm text-center text-[#6B6B6B] mt-6">
                Remembered it?{" "}
                <Link to="/login" className="text-ink font-semibold" data-testid="forgot-password-login-link">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
